import styled, { DefaultTheme } from 'styled-components';
import { Button, Modal, Tabs } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const TableCard = styled.div`
  background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
`;

export const TableHeader = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 8px;
  margin-bottom: 8px;
`;

export const TableHeaderCell = styled.div`
  font-weight: 700;
  text-transform: uppercase;
  padding: 12px 16px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  font-size: 12px;
  letter-spacing: 0.5px;
`;

export const TableBody = styled.div<{ columns: string; minWidth: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  min-width: ${(props) => props.minWidth};
`;

export const TableRow = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 6px;
  background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#f0f0f0')};
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
    border-color: #1890ff;
  }
`;

export const TableCell = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-size: 14px;
`;

export const TemplateNameCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const TemplateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TemplateName = styled.span`
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  display: block;
`;

export const TemplateSubject = styled.span`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')};
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
`;

export const TemplateDescription = styled.span`
  font-size: 13px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const PreviewButton = styled.button`
  background: none;
  border: none;
  color: #1890ff;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => getThemeValue(props, '#262626', '#f0f0f0')};
    color: #40a9ff;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 32px;
  text-align: center;
  min-height: 300px;
`;

export const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${(props) => getThemeValue(props, '#595959', '#bfbfbf')};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

export const EmptyTitle = styled.div`
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`;

export const EmptyDescription = styled.div`
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  font-size: 14px;
  max-width: 300px;
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    font-size: 16px;
    font-weight: 600;
  }

  .ant-modal-body {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }

  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }

  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};

    &:hover {
      color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
    }
  }

  .ant-tabs-tab-active {
    color: ${(props) => getThemeValue(props, '#ffffff', '#1890ff')};
  }

  .ant-tabs-ink-bar {
    background: #1890ff;
  }

  .ant-tabs-content {
    background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  }
`;

export const PreviewContent = styled.div`
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 6px;
  min-height: 400px;
  background: #ffffff;
  color: #000000;
  overflow: auto;

  iframe {
    width: 100%;
    height: 500px;
    border: none;
    border-radius: 6px;
  }
`;

export const CodeBlock = styled.pre`
  background: ${(props) => getThemeValue(props, '#262626', '#fafafa')};
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  padding: 16px;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
  overflow: auto;
  max-height: 500px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
`;

export const MobileCard = styled.div`
  background: ${(props) => getThemeValue(props, '#1f1f1f', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

export const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const MobileCardTitle = styled.div`
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const MobileCardDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DetailLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')};
  letter-spacing: 0.5px;
`;

export const DetailValue = styled.span`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#000000')};
`;

export const MenuButton = styled(Button)`
  background: transparent !important;
  border: none !important;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#595959')} !important;
  cursor: pointer;
  padding: 4px 8px !important;
  border-radius: 4px !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: ${(props) => getThemeValue(props, '#262626', '#f0f0f0')} !important;
    color: ${(props) => getThemeValue(props, '#ffffff', '#000000')} !important;
  }
`;

export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#eee')};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, 'transparent', 'inherit')};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || '#2D6CCA';
      if (iconColor === '#f5222d') {
        return getThemeValue(props, 'rgba(245, 34, 45, 0.2)', 'rgba(245, 34, 45, 0.1)');
      }
      return getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(45, 108, 202, 0.1)');
    }} !important;

    svg {
      color: ${(props) => props.iconColor || '#2D6CCA'} !important;
    }
  }
`;
